import posthog from "posthog-js";
import { config } from "./config";
import {
  DEFAULT_FUNNEL_CONCEPT,
  getFunnelConceptFromLocation,
  getStoredOrDefaultFunnelConcept,
  type FunnelConceptKey,
} from "./funnelConcept";
import { buildFunnelPageviewPayload, type FunnelRouteOptions } from "./funnelRoutes";

type FbqFunction = (
  command: "init" | "track" | "set",
  eventNameOrPixelIdOrSetKey: string,
  parameters?: Record<string, unknown>,
  options?: Record<string, unknown>,
) => void;

type FbqWithQueue = FbqFunction & {
  callMethod?: FbqFunction;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
};

type WindowWithMeta = Window &
  typeof globalThis & {
    fbq?: FbqWithQueue;
    _fbq?: unknown;
    __agentmaxMetaPixelInit?: boolean;
  };

export type TrackingContext = {
  eventId: string;
  fbp: string | null;
  fbc: string | null;
  externalId: string;
};

const PENDING_CHECKOUT_STORAGE_KEY = "agentmax:pending-checkout";
const LAST_FUNNEL_PAGEVIEW_URL_KEY = "__agentmax_last_funnel_pageview_url__";
const META_EXTERNAL_ID_STORAGE_KEY = "agentmax:meta-external-id";
// Shared with agentmax.dev so a visitor stitched into one Meta identity across surfaces.
const META_EXTERNAL_ID_COOKIE = "agentmax_ext_id";
const META_FBP_STORAGE_KEY = "agentmax:meta-fbp";
const META_FBC_STORAGE_KEY = "agentmax:meta-fbc";
const META_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const FUNNEL_STORAGE_KEY = "agentmax:funnel:v2";
const POSTHOG_QA_ATTRIBUTE = "data-agentmax-posthog-qa";

let runtimeAnalyticsContext: Record<string, unknown> = {};

const buildPosthogProperties = (properties: Record<string, unknown> = {}): Record<string, unknown> => ({
  product: "agentmax",
  funnel: "agentmax_webfunnel",
  ...runtimeAnalyticsContext,
  ...properties,
});

const recordPosthogEventForLocalQa = (
  eventName: string,
  properties: Record<string, unknown>,
): void => {
  if (!config.posthogQaDebug || typeof document === "undefined") return;
  let events: Array<{ eventName: string; properties: Record<string, unknown> }> = [];
  try {
    events = JSON.parse(document.documentElement.getAttribute(POSTHOG_QA_ATTRIBUTE) || "[]") as typeof events;
  } catch {
    // Replace malformed test-only state instead of affecting analytics.
  }
  events.push({ eventName, properties });
  document.documentElement.setAttribute(POSTHOG_QA_ATTRIBUTE, JSON.stringify(events));
};

/**
 * Adds page-scoped properties to every event without persisting them as
 * PostHog super-properties. The cleanup guard prevents one route from leaking
 * sales-letter attribution into a later quiz visit in the same browser.
 */
export const setRuntimeAnalyticsContext = (properties: Record<string, unknown>): (() => void) => {
  const nextContext = { ...properties };
  runtimeAnalyticsContext = nextContext;
  return () => {
    if (runtimeAnalyticsContext === nextContext) runtimeAnalyticsContext = {};
  };
};

export const initAnalytics = (): void => {
  if (typeof window === "undefined") return;
  persistMetaIdentifiers();
  if (!config.metaPixelId) return;

  const w = window as WindowWithMeta;
  if (w.__agentmaxMetaPixelInit) return;
  w.__agentmaxMetaPixelInit = true;

  if (!w.fbq) {
    const fbq = ((command, eventNameOrPixelId, parameters, options) => {
      if (fbq.callMethod) {
        fbq.callMethod(command, eventNameOrPixelId, parameters, options);
      } else {
        fbq.queue?.push([command, eventNameOrPixelId, parameters, options]);
      }
    }) as FbqWithQueue;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    w.fbq = fbq;
    w._fbq = fbq;
    if (config.loadMetaScript) {
      const script = document.createElement("script");
      script.async = true;
      script.src = config.metaScriptUrl;
      document.head.appendChild(script);
    }
  }

  w.fbq?.("init", config.metaPixelId, { external_id: getOrCreateExternalId() });
  scheduleFbpMirror();
};

export const track = (eventName: string, properties: Record<string, unknown> = {}): void => {
  const payload = buildPosthogProperties(properties);
  recordPosthogEventForLocalQa(eventName, payload);
  if (config.posthogKey) {
    posthog.capture(eventName, payload);
  }
};

export const capturePageview = (properties: Record<string, unknown> = {}): void => {
  if (typeof window === "undefined") return;
  const payload = buildPosthogProperties({
    $current_url: window.location.href,
    path: window.location.pathname,
    ...properties,
  });
  recordPosthogEventForLocalQa("$pageview", payload);
  if (config.posthogKey) posthog.capture("$pageview", payload);
};

export const captureFunnelScreenPageview = (
  step: number,
  extras: { recommendedPlan: string; entryConceptKey: FunnelConceptKey; entryConceptLabel: string },
  options?: FunnelRouteOptions,
): void => {
  if (!config.posthogKey || typeof window === "undefined") return;
  const payload = buildFunnelPageviewPayload(step, extras, options);
  const w = window as Window & { [LAST_FUNNEL_PAGEVIEW_URL_KEY]?: string };
  if (w[LAST_FUNNEL_PAGEVIEW_URL_KEY] === payload.$current_url) return;
  w[LAST_FUNNEL_PAGEVIEW_URL_KEY] = String(payload.$current_url);
  posthog.capture("$pageview", payload);
};

export const registerFunnelPersonProperties = (properties: Record<string, string | number | boolean>): void => {
  if (!config.posthogKey) return;
  posthog.register(properties);
};

const getCurrentFunnelConcept = () => {
  if (typeof window === "undefined") return DEFAULT_FUNNEL_CONCEPT;
  const conceptFromUrl = getFunnelConceptFromLocation(window.location);
  if (conceptFromUrl) return conceptFromUrl;
  try {
    const raw = window.localStorage.getItem(FUNNEL_STORAGE_KEY);
    if (!raw) return DEFAULT_FUNNEL_CONCEPT;
    const parsed = JSON.parse(raw) as { entryConceptKey?: string };
    return getStoredOrDefaultFunnelConcept(parsed.entryConceptKey);
  } catch {
    return DEFAULT_FUNNEL_CONCEPT;
  }
};

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

// Scope to the registrable domain so start.agentmax.dev and agentmax.dev share the value.
const sharedCookieDomain = (): string => {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname.toLowerCase();
  if (host === "agentmax.dev" || host.endsWith(".agentmax.dev")) return "; Domain=.agentmax.dev";
  return "";
};

const setSharedCookie = (name: string, value: string): void => {
  if (typeof document === "undefined" || !value) return;
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${META_COOKIE_MAX_AGE_SECONDS}; Path=/${sharedCookieDomain()}; SameSite=Lax${secure}`;
};

const readStorage = (key: string): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key)?.trim() || null;
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    //
  }
};

const buildMetaClickId = (fbclid: string): string => `fb.1.${Date.now()}.${fbclid}`;

const persistMetaIdentifiers = (): void => {
  if (typeof window === "undefined") return;
  const fbclid = new URL(window.location.href).searchParams.get("fbclid")?.trim();
  if (fbclid) {
    const fbc = buildMetaClickId(fbclid);
    setSharedCookie("_fbc", fbc);
    writeStorage(META_FBC_STORAGE_KEY, fbc);
  }
  const fbpFromCookie = getCookie("_fbp");
  if (fbpFromCookie) {
    setSharedCookie("_fbp", fbpFromCookie);
    writeStorage(META_FBP_STORAGE_KEY, fbpFromCookie);
  }
  const fbcFromCookie = getCookie("_fbc");
  if (fbcFromCookie) {
    setSharedCookie("_fbc", fbcFromCookie);
    writeStorage(META_FBC_STORAGE_KEY, fbcFromCookie);
  }
};

const scheduleFbpMirror = (): void => {
  if (typeof window === "undefined") return;
  let attempts = 0;
  const tick = () => {
    const fbp = getCookie("_fbp");
    if (fbp) {
      setSharedCookie("_fbp", fbp);
      writeStorage(META_FBP_STORAGE_KEY, fbp);
      return;
    }
    if (attempts++ < 20) window.setTimeout(tick, 150);
  };
  window.setTimeout(tick, 150);
};

export const waitForFbp = (timeoutMs = 1500, intervalMs = 100): Promise<string | null> => {
  if (typeof window === "undefined") return Promise.resolve(null);
  const existing = getCookie("_fbp") ?? readStorage(META_FBP_STORAGE_KEY);
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve) => {
    const start = Date.now();
    const poll = () => {
      const fbp = getCookie("_fbp");
      if (fbp) {
        setSharedCookie("_fbp", fbp);
        writeStorage(META_FBP_STORAGE_KEY, fbp);
        resolve(fbp);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(readStorage(META_FBP_STORAGE_KEY));
        return;
      }
      window.setTimeout(poll, intervalMs);
    };
    window.setTimeout(poll, intervalMs);
  });
};

const generateEventId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const getFbq = (): FbqWithQueue | null => {
  if (!config.metaPixelId || typeof window === "undefined") return null;
  const fbq = (window as WindowWithMeta).fbq;
  return typeof fbq === "function" ? fbq : null;
};

// A deliberately inert local test script can leave the standard Meta queue on
// `_fbq` while replacing `window.fbq`. Prefer the live function, then fall back
// to that queue so event-contract QA stays deterministic without contacting Meta.
const getQueuedFbq = (): FbqWithQueue | null => {
  const active = getFbq();
  if (active) return active;
  if (typeof window === "undefined") return null;
  const queued = (window as WindowWithMeta)._fbq;
  return typeof queued === "function" ? queued as FbqWithQueue : null;
};

const recordMetaEventForLocalQa = (eventName: string, payload: Record<string, unknown>): void => {
  if (!config.metaQaDebug || typeof document === "undefined") return;
  const attribute = "data-agentmax-meta-qa";
  let events: Array<Record<string, unknown>> = [];
  try {
    events = JSON.parse(document.documentElement.getAttribute(attribute) || "[]") as Array<Record<string, unknown>>;
  } catch {
    // Replace malformed test-only state instead of affecting analytics.
  }
  events.push({ eventName, ...payload });
  document.documentElement.setAttribute(attribute, JSON.stringify(events));
};

const getOrCreateExternalId = (): string => {
  // Prefer the shared cookie so an id minted on agentmax.dev carries into the funnel.
  const existing = getCookie(META_EXTERNAL_ID_COOKIE) ?? readStorage(META_EXTERNAL_ID_STORAGE_KEY);
  const externalId = existing || `anon-${generateEventId()}`;
  setSharedCookie(META_EXTERNAL_ID_COOKIE, externalId);
  writeStorage(META_EXTERNAL_ID_STORAGE_KEY, externalId);
  return externalId;
};

export const upgradeExternalIdToLead = (leadId: number): void => {
  if (!Number.isInteger(leadId) || leadId <= 0) return;
  const upgraded = `lead-${leadId}`;
  if (readStorage(META_EXTERNAL_ID_STORAGE_KEY) === upgraded) return;
  setSharedCookie(META_EXTERNAL_ID_COOKIE, upgraded);
  writeStorage(META_EXTERNAL_ID_STORAGE_KEY, upgraded);
  if (config.metaPixelId) getFbq()?.("init", config.metaPixelId, { external_id: upgraded });
};

export const getTrackingContext = (): TrackingContext => ({
  eventId: generateEventId(),
  fbp: getCookie("_fbp") ?? readStorage(META_FBP_STORAGE_KEY),
  fbc: getCookie("_fbc") ?? readStorage(META_FBC_STORAGE_KEY),
  externalId: getOrCreateExternalId(),
});

export const firePageViewBrowser = (eventId: string): void => {
  recordMetaEventForLocalQa("PageView", { eventId });
  getQueuedFbq()?.("track", "PageView", {}, { eventID: eventId });
};

export const trackMetaViewContent = (payload: {
  eventId: string;
  contentIds: string[];
  value: number;
  currency: string;
  contentName?: string;
}): void => {
  const parameters: Record<string, unknown> = {
    content_ids: payload.contentIds,
    content_type: "product",
    currency: payload.currency.trim().toUpperCase(),
    value: payload.value,
  };
  if (payload.contentName?.trim()) parameters.content_name = payload.contentName.trim();
  recordMetaEventForLocalQa("ViewContent", { eventId: payload.eventId, parameters });
  const fbq = getQueuedFbq();
  if (!fbq) return;
  fbq("track", "ViewContent", parameters, { eventID: payload.eventId });
};

export const trackMetaLead = (eventId: string, options?: { email?: string }): void => {
  recordMetaEventForLocalQa("Lead", { eventId });
  const fbq = getQueuedFbq();
  if (!fbq) return;
  const email = options?.email?.trim().toLowerCase();
  if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && config.metaPixelId) {
    fbq("init", config.metaPixelId, { em: email });
  }
  fbq("track", "Lead", {}, { eventID: eventId });
};

export const trackMetaInitiateCheckout = (
  eventId: string,
  planId: string,
  value: number,
  currency: string,
): void => {
  const parameters = {
    content_ids: [planId],
    content_type: "product",
    currency: currency.trim().toUpperCase(),
    value,
  };
  recordMetaEventForLocalQa("InitiateCheckout", { eventId, parameters });
  const fbq = getQueuedFbq();
  if (!fbq) return;
  fbq(
    "track",
    "InitiateCheckout",
    parameters,
    { eventID: eventId },
  );
};

export const rememberPendingCheckoutTracking = (tracking: {
  sessionId: string;
  eventId: string;
  planId: string;
  value: number;
  currency: string;
  completionToken?: string;
}): void => {
  sessionStorage.setItem(PENDING_CHECKOUT_STORAGE_KEY, JSON.stringify({ ...tracking, createdAt: Date.now() }));
};

export const getPendingCheckoutTracking = (sessionId: string) => {
  try {
    const raw = sessionStorage.getItem(PENDING_CHECKOUT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || parsed.sessionId !== sessionId) return null;
    return parsed;
  } catch {
    return null;
  }
};

export { getCurrentFunnelConcept };
